/**
 * Error Context
 *
 * React context for centralized error state management.
 * Provides error tracking, display, and integration with ErrorBoundary.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
  ReactNode,
  ErrorInfo as ReactErrorInfo,
} from 'react';

import {
  AppError,
  ErrorSeverity,
  isAppError,
} from './errorTypes';
import {
  ErrorInfo,
  handleError,
  getErrorMessage,
  registerGlobalErrorHandler,
  unregisterGlobalErrorHandler,
} from './errorHandler';
import { reportError, ErrorReportContext } from './errorReporter';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Error entry in the error list.
 */
export interface ErrorEntry {
  /** Unique identifier for this error entry */
  id: string;
  /** Normalized error information */
  info: ErrorInfo;
  /** Whether the error has been dismissed by the user */
  dismissed: boolean;
  /** Additional context about where the error occurred */
  context?: string;
}

/**
 * Error context state.
 */
export interface ErrorContextState {
  /** List of current errors */
  errors: ErrorEntry[];
  /** Most recent error (if any) */
  lastError: ErrorEntry | null;
  /** Whether there are any undismissed errors */
  hasErrors: boolean;
  /** Count of undismissed errors */
  errorCount: number;
}

/**
 * Error context actions.
 */
export interface ErrorContextActions {
  /** Adds a new error to the context */
  addError: (error: unknown, context?: string) => string;
  /** Removes an error by ID */
  removeError: (id: string) => void;
  /** Dismisses an error (marks as read but keeps in history) */
  dismissError: (id: string) => void;
  /** Dismisses all errors */
  dismissAllErrors: () => void;
  /** Clears all errors from the context */
  clearAllErrors: () => void;
  /** Gets an error by ID */
  getError: (id: string) => ErrorEntry | undefined;
  /** Handler for ErrorBoundary integration */
  handleBoundaryError: (error: Error, errorInfo: ReactErrorInfo) => void;
}

/**
 * Complete error context value.
 */
export type ErrorContextValue = ErrorContextState & ErrorContextActions;

// =============================================================================
// CONTEXT
// =============================================================================

const ErrorContext = createContext<ErrorContextValue | null>(null);

// =============================================================================
// PROVIDER PROPS
// =============================================================================

export interface ErrorProviderProps {
  /** Child components */
  children: ReactNode;
  /** Maximum number of errors to keep in history */
  maxErrors?: number;
  /** Auto-dismiss errors after this many milliseconds (0 to disable) */
  autoDismissAfter?: number;
  /** Only auto-dismiss errors below this severity level */
  autoDismissBelowSeverity?: ErrorSeverity;
  /** Enable global error handler registration */
  captureGlobalErrors?: boolean;
  /** Enable error reporting to external services */
  enableReporting?: boolean;
  /** Callback when a new error is added */
  onError?: (entry: ErrorEntry) => void;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generates a unique ID for error entries.
 */
function generateErrorId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Determines if an error should be auto-dismissed based on severity.
 */
function shouldAutoDismiss(
  severity: ErrorSeverity,
  threshold: ErrorSeverity
): boolean {
  const severityOrder: ErrorSeverity[] = [
    ErrorSeverity.LOW,
    ErrorSeverity.MEDIUM,
    ErrorSeverity.HIGH,
    ErrorSeverity.CRITICAL,
  ];

  const errorIndex = severityOrder.indexOf(severity);
  const thresholdIndex = severityOrder.indexOf(threshold);

  return errorIndex < thresholdIndex;
}

// =============================================================================
// PROVIDER COMPONENT
// =============================================================================

/**
 * Error context provider component.
 */
export function ErrorProvider({
  children,
  maxErrors = 50,
  autoDismissAfter = 0,
  autoDismissBelowSeverity = ErrorSeverity.MEDIUM,
  captureGlobalErrors = true,
  enableReporting = true,
  onError,
}: ErrorProviderProps): React.JSX.Element {
  const [errors, setErrors] = useState<ErrorEntry[]>([]);

  /**
   * Adds a new error to the context.
   */
  const addError = useCallback(
    (error: unknown, context?: string): string => {
      const id = generateErrorId();
      const info = handleError(error);

      const entry: ErrorEntry = {
        id,
        info,
        dismissed: false,
        context,
      };

      setErrors((prev) => {
        const updated = [entry, ...prev];
        // Limit the number of stored errors
        return updated.slice(0, maxErrors);
      });

      // Report to external services if enabled
      if (enableReporting && info.originalError) {
        const reportContext: ErrorReportContext = {
          componentStack: context,
          userAction: context,
          additionalData: info.metadata,
        };
        reportError(info.originalError, reportContext);
      }

      // Notify callback
      if (onError) {
        onError(entry);
      }

      return id;
    },
    [maxErrors, enableReporting, onError]
  );

  /**
   * Removes an error by ID.
   */
  const removeError = useCallback((id: string): void => {
    setErrors((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  /**
   * Dismisses an error.
   */
  const dismissError = useCallback((id: string): void => {
    setErrors((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, dismissed: true } : entry
      )
    );
  }, []);

  /**
   * Dismisses all errors.
   */
  const dismissAllErrors = useCallback((): void => {
    setErrors((prev) => prev.map((entry) => ({ ...entry, dismissed: true })));
  }, []);

  /**
   * Clears all errors.
   */
  const clearAllErrors = useCallback((): void => {
    setErrors([]);
  }, []);

  /**
   * Gets an error by ID.
   */
  const getError = useCallback(
    (id: string): ErrorEntry | undefined => {
      return errors.find((entry) => entry.id === id);
    },
    [errors]
  );

  /**
   * Handler for ErrorBoundary integration.
   */
  const handleBoundaryError = useCallback(
    (error: Error, errorInfo: ReactErrorInfo): void => {
      addError(error, errorInfo.componentStack ?? 'ErrorBoundary');
    },
    [addError]
  );

  /**
   * Register global error handler.
   */
  useEffect(() => {
    if (!captureGlobalErrors) {
      return;
    }

    const handler = (errorInfo: ErrorInfo): void => {
      const id = generateErrorId();
      const entry: ErrorEntry = {
        id,
        info: errorInfo,
        dismissed: false,
        context: 'Global Error Handler',
      };

      setErrors((prev) => {
        const updated = [entry, ...prev];
        return updated.slice(0, maxErrors);
      });

      if (onError) {
        onError(entry);
      }
    };

    registerGlobalErrorHandler(handler);

    return () => {
      unregisterGlobalErrorHandler(handler);
    };
  }, [captureGlobalErrors, maxErrors, onError]);

  /**
   * Auto-dismiss errors based on configuration.
   */
  useEffect(() => {
    if (autoDismissAfter <= 0) {
      return;
    }

    const timers: NodeJS.Timeout[] = [];

    errors.forEach((entry) => {
      if (
        !entry.dismissed &&
        shouldAutoDismiss(entry.info.severity, autoDismissBelowSeverity)
      ) {
        const timer = setTimeout(() => {
          dismissError(entry.id);
        }, autoDismissAfter);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [errors, autoDismissAfter, autoDismissBelowSeverity, dismissError]);

  /**
   * Computed state values.
   */
  const undismissedErrors = useMemo(
    () => errors.filter((entry) => !entry.dismissed),
    [errors]
  );

  const lastError = useMemo(
    () => (undismissedErrors.length > 0 ? undismissedErrors[0] : null),
    [undismissedErrors]
  );

  const contextValue = useMemo<ErrorContextValue>(
    () => ({
      errors,
      lastError,
      hasErrors: undismissedErrors.length > 0,
      errorCount: undismissedErrors.length,
      addError,
      removeError,
      dismissError,
      dismissAllErrors,
      clearAllErrors,
      getError,
      handleBoundaryError,
    }),
    [
      errors,
      lastError,
      undismissedErrors.length,
      addError,
      removeError,
      dismissError,
      dismissAllErrors,
      clearAllErrors,
      getError,
      handleBoundaryError,
    ]
  );

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to access the error context.
 * Must be used within an ErrorProvider.
 */
export function useError(): ErrorContextValue {
  const context = useContext(ErrorContext);

  if (!context) {
    throw new Error(
      'useError deve essere utilizzato all\'interno di un ErrorProvider'
    );
  }

  return context;
}

/**
 * Hook to add errors without subscribing to state changes.
 * Useful for components that only need to report errors.
 */
export function useErrorReporter(): Pick<
  ErrorContextActions,
  'addError' | 'handleBoundaryError'
> {
  const { addError, handleBoundaryError } = useError();
  return { addError, handleBoundaryError };
}

/**
 * Hook to get the last error message.
 * Returns null if there are no errors.
 */
export function useLastErrorMessage(): string | null {
  const { lastError } = useError();
  return lastError ? getErrorMessage(lastError.info.originalError) : null;
}

// =============================================================================
// HIGHER-ORDER COMPONENT
// =============================================================================

/**
 * Props injected by withErrorHandling HOC.
 */
export interface WithErrorHandlingProps {
  onError: (error: unknown, context?: string) => string;
}

/**
 * HOC that injects error handling capabilities into a component.
 */
export function withErrorHandling<P extends object>(
  WrappedComponent: React.ComponentType<P & WithErrorHandlingProps>
): React.FC<P> {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const WithErrorHandlingComponent: React.FC<P> = (props) => {
    const { addError } = useError();

    return <WrappedComponent {...props} onError={addError} />;
  };

  WithErrorHandlingComponent.displayName = `withErrorHandling(${displayName})`;

  return WithErrorHandlingComponent;
}

// =============================================================================
// UTILITY COMPONENTS
// =============================================================================

/**
 * Component that displays the last error as a toast/notification.
 */
export interface ErrorToastProps {
  /** Custom render function for the toast */
  render?: (entry: ErrorEntry, onDismiss: () => void) => ReactNode;
  /** Position of the toast */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** Custom styles */
  style?: React.CSSProperties;
}

export function ErrorToast({
  render,
  position = 'top-right',
  style,
}: ErrorToastProps): React.JSX.Element | null {
  const { lastError, dismissError } = useError();

  if (!lastError) {
    return null;
  }

  const handleDismiss = (): void => {
    dismissError(lastError.id);
  };

  if (render) {
    return <>{render(lastError, handleDismiss)}</>;
  }

  const positionStyles: Record<string, React.CSSProperties> = {
    'top-right': { top: 16, right: 16 },
    'top-left': { top: 16, left: 16 },
    'bottom-right': { bottom: 16, right: 16 },
    'bottom-left': { bottom: 16, left: 16 },
  };

  const severityColors: Record<ErrorSeverity, string> = {
    [ErrorSeverity.LOW]: '#fef3c7',
    [ErrorSeverity.MEDIUM]: '#fee2e2',
    [ErrorSeverity.HIGH]: '#fecaca',
    [ErrorSeverity.CRITICAL]: '#f87171',
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        ...positionStyles[position],
        maxWidth: 400,
        padding: 16,
        backgroundColor: severityColors[lastError.info.severity],
        borderRadius: 8,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        zIndex: 9999,
        ...style,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <strong style={{ display: 'block', marginBottom: 4 }}>Errore</strong>
          <span>{lastError.info.message}</span>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Chiudi notifica errore"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            marginLeft: 8,
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          &times;
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// ERROR BOUNDARY INTEGRATION
// =============================================================================

/**
 * Props for ErrorBoundaryWithContext.
 */
export interface ErrorBoundaryWithContextProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error) => ReactNode);
}

/**
 * Wrapper component that connects ErrorBoundary to ErrorContext.
 * This component should wrap areas where you want to catch and report errors.
 */
export function ErrorBoundaryWithContext({
  children,
  fallback,
}: ErrorBoundaryWithContextProps): React.JSX.Element {
  const { handleBoundaryError } = useError();

  // Import ErrorBoundary dynamically to avoid circular dependency
  // Use React's built-in error boundary pattern
  return (
    <ErrorBoundaryInternal
      fallback={fallback}
      onError={handleBoundaryError}
    >
      {children}
    </ErrorBoundaryInternal>
  );
}

/**
 * Internal error boundary class component.
 */
interface ErrorBoundaryInternalProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error) => ReactNode);
  onError?: (error: Error, errorInfo: ReactErrorInfo) => void;
}

interface ErrorBoundaryInternalState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryInternal extends React.Component<
  ErrorBoundaryInternalProps,
  ErrorBoundaryInternalState
> {
  constructor(props: ErrorBoundaryInternalProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryInternalState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ReactErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error);
      }
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div
          style={{
            padding: 24,
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            color: '#991b1b',
          }}
        >
          <h3 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600 }}>
            Si è verificato un errore
          </h3>
          <p style={{ margin: 0, fontSize: 14, opacity: 0.9 }}>
            {this.state.error.message}
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
